import { useMemo } from "react";
import useReferenceApi from "@services/api/reference/reference.api";
import { Reference } from "@services/api/reference/reference.types";
import useYearStore from "@src/providers/year.store";

type UseReferenceProps = {
  type?: Reference["type"];
  visibleExternalIds?: string[];
};

export default function useReferences({
  type,
  visibleExternalIds
}: UseReferenceProps) {
  const { currentYear } = useYearStore();

  const { useReferenceGroupedQuery } = useReferenceApi();

  const { data: referencesData } = useReferenceGroupedQuery({
    variables: {
      year: currentYear?.externalId
    }
  });

  const references: Reference[] = useMemo(() => {
    if (!referencesData || !type) {
      return [];
    }
    const referencesForType = referencesData[type] || [];
    if (!visibleExternalIds) {
      return referencesForType;
    }
    return referencesForType.filter((ref) =>
      visibleExternalIds.includes(ref.externalId)
    );
  }, [referencesData, type, visibleExternalIds]);

  return {
    references
  };
}

export function useAllReferences() {
  const { currentYear } = useYearStore();
  const { useReferenceGroupedQuery } = useReferenceApi();

  const { data: referencesData } = useReferenceGroupedQuery({
    variables: {
      year: currentYear?.externalId
    }
  });

  const allReferences: Reference[] = useMemo(() => {
    if (!referencesData) {
      return [];
    }
    return Object.values(referencesData).flat();
  }, [referencesData]);

  return allReferences;
}
